/**
 * HistoryPanel - 历史会话侧边栏
 * 
 * 显示当前智能体的历史对话列表，支持：
 * - 切换到历史会话
 * - 新建对话
 * - 删除会话（hover 显示删除按钮，确认后删除）
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const HistoryPanel = ({
  sessions,
  isLoading,
  currentSessionId,
  onSelect,
  onNewChat,
  onClose,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState(null); // 正在确认删除的会话 ID
  const [isDeleting, setIsDeleting] = useState(false); // 删除中

  const handleDeleteClick = useCallback((e, sessionId) => {
    e.stopPropagation(); // 阻止触发 onSelect
    if (deletingId === sessionId) {
      // 第二次点击 = 确认删除
      setIsDeleting(true);
      onDelete(sessionId).then(() => {
        setDeletingId(null);
        setIsDeleting(false);
      }).catch(() => {
        setDeletingId(null);
        setIsDeleting(false);
      });
    } else {
      // 第一次点击 = 进入确认状态
      setDeletingId(sessionId);
      // 3 秒后自动取消确认状态
      setTimeout(() => setDeletingId(prev => prev === sessionId ? null : prev), 3000);
    }
  }, [deletingId, onDelete]);

  return (
    <div className="agent-chat-history-panel">
      <div className="agent-chat-history-header">
        <h4>{t('history_title')}</h4>
        <button className="agent-chat-history-close" onClick={onClose} title={t('history_close')}>
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <button className="agent-chat-history-new" onClick={onNewChat}>
        <AddIcon fontSize="small" />
        <span>{t('history_new')}</span>
      </button>

      <div className="agent-chat-history-list">
        {isLoading && sessions.length === 0 ? (
          <div className="agent-chat-history-loading">
            <span>{t('history_loading')}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="agent-chat-history-empty">
            <ChatBubbleOutlineIcon fontSize="small" />
            <span>{t('history_empty')}</span>
          </div>
        ) : (
          sessions.map((session) => {
            const sid = session.session_id || session.id;
            const isActive = currentSessionId === sid;
            const isConfirmDelete = deletingId === sid;

            return (
              <div
                key={sid}
                className={`agent-chat-history-item ${isActive ? 'active' : ''}`}
                onClick={() => onSelect(sid)}
              >
                <ChatBubbleOutlineIcon fontSize="small" className="history-item-icon" />
                <div className="history-item-info">
                  <span className="history-item-preview">
                    {session.last_message || t('history_record')}
                  </span>
                  <span className="history-item-meta">
                    <span className="history-item-time">
                      {formatSessionTime(session.last_active || session.updated_at || session.created_at, t)}
                    </span>
                    {session.message_count > 0 && (
                      <span className="history-item-count">{t('history_count', { count: session.message_count })}</span>
                    )}
                  </span>
                </div>
                {onDelete && (
                  <button
                    className={`history-item-delete ${isConfirmDelete ? 'confirm' : ''}`}
                    onClick={(e) => handleDeleteClick(e, sid)}
                    disabled={isDeleting}
                    title={isConfirmDelete ? t('history_delete_confirm') : t('history_delete')}
                  >
                    {isConfirmDelete ? (
                      <span className="delete-confirm-text">{t('history_delete_confirm_text')}</span>
                    ) : (
                      <DeleteOutlineIcon fontSize="small" />
                    )}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

function formatSessionTime(timestamp, t) {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return t('history_time_just_now');
    if (diffMin < 60) return t('history_time_minutes', { count: diffMin });
    if (diffHour < 24) return t('history_time_hours', { count: diffHour });
    if (diffDay < 7) return t('history_time_days', { count: diffDay });
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default React.memo(HistoryPanel);
